<template>
  <div :class="['tenancy-all-ecircular']">
    <TMSHeader
      :title="$t('eCircular.alleCircular')"
      :buttons="[refreshBtn]"
      :needGoback="true"
      :fullScreen="true"
    >
      <template slot="back-button">
        <span
          class="back"
          @click="navBack()"
        >
          &lt; {{ $t('back') }}
        </span>
      </template>
    </TMSHeader>
    <div class="all-list">
      <div class="history-content">
        <div
          v-if="eCircularSearchCount"
          class="total-count"
        >
          <span>{{ $t('total') }}: {{ eCircularSearchCount }}</span>
        </div>
        <div class="search-results">
          <HKLTable
            ref="table"
            :dataSource="orderedItems"
            :columns="tableColumns"
            :sort="eCircularTableOrder.orderKey || 'sentAt'"
            :filterBar="true"
            :onFilter="false"
            @filter="onFilter"
            @loadmore="loadMore"
          />
        </div>
      </div>
    </div>
    <Modal
      ref="eCircularContentModal"
      modalType="large-modal"
      :showCloseBtn="false"
      :modalId="eCircularContentModalId"
    >
      <div slot="content">
        <ECircularPreviewIndex
          v-if="showingEcircular.id"
          :key="showingEcircular.id"
          :eCircular="showingEcircular"
        />
        <div
          v-show="!isEmptyAttachments"
          class="attachments input-row"
        >
          <label>{{ $t('attachments') }}</label>
          <EditAttachments
            :canEdit="false"
            :canDelete="false"
            :sourceId="sourceId"
            :getFileLengthID="sourceId"
            :canDownloadAll="true"
            :zipName="`${showingEcircular.subject}_eCircular_Documents.zip`"
          />
        </div>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="closeECircularContentModal()"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash, { cloneDeep, unionBy } from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import ECircularPreviewIndex from '@/modules/eCircular/ECircularViews/ECircularPreviewIndex'
import EditAttachments from '@/controls/EditAttachments'
import Modal from '@/controls/Modal'
import TMSHeader from '@/controls/TMSHeader'
import HKLTable from '@/controls/Table/HKLTable'
import { ALL_VALUE } from '@/controls/Select/HKLMultiSelect'
import * as ecircularAPI from '@/api/ecircular/request'
import { getTenancy } from '@/api/tenancies/request'
import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'

import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { ccdRouteNames } from '@/utils/consts'
import { E_CIRCULAR_TYPES, E_CIRCULAR_STATUS } from '@/modules/eCircular/consts'
import { getBuildingFloorZoneMap } from '@/api/buildings/request'
import ECircularColumnsMixin from '../../eCircular/ECircularColumnsMixin'

export default {
  name: 'TenancyAllECircular',
  components: {
    TMSHeader,
    Modal,
    ECircularPreviewIndex,
    EditAttachments,
    HKLTable,
  },
  mixins: [ECircularColumnsMixin],
  data () {
    return {
      showingEcircular: { id: '' },
      eCircularContentModalId: `ecircular-content-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      tenancyId: null,
      isEmptyAttachments: false,
      BuildingFloorZoneMap: null,
      searchAttributes: {
        limit: 100,
        offset: 0,
        tenancyId: null,
      },
      refreshBtn: {
        title: this.$t('action.search'),
        cls: 'btn--orange',
        func: this.search,
      },
      interactionFunc: {
        code: this.gotoECircularDetail,
        subject: this.gotoECircularDetail,
      },
      totalLength: 0,
      inTenancyAllTableHeaders: [],
      filterObject: {},
    }
  },
  computed: {
    ...mapGetters('ccd', ['lastECircularSearchAttrs', 'lastSearchedECirculars', 'eCircularSearchCount', 'lastSearchedCategories',
      'eCircularFilterConditions', 'eCircularTableOrder']),
    ...mapGetters('user', ['currentUser', 'isTenant']),
    ...mapGetters('categories', {
      rawCategoryOptions: 'categoryOptions',
    }),
    canCreateUpdate () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.createUpdate)
    },
    canRead () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.read)
    },
    orderedItems () {
      return this.lastSearchedECirculars.map(item => {
        const eCircular = cloneDeep(item)
        const sentHistory = eCircular.histories.filter(history => [E_CIRCULAR_STATUS.SENT, E_CIRCULAR_STATUS.SENDING].includes(history.status))[0]
        eCircular.sentAt = sentHistory && sentHistory.createdAt
        return eCircular
      })
    },
    showLoadMore () {
      return this.lastSearchedECirculars.length > 0 && this.lastSearchedECirculars.length < this.eCircularSearchCount
    },
    sourceId () {
      const id = this.showingEcircular.id || ''
      if (id) {
        this.$refs.eCircularContentModal.$on(`controls.hkldDropzone.loadFilesLength${id}`, (filesCount) => {
          this.isEmptyAttachments = !filesCount
        })
      }
      return id
    },
    categoryOptions () {
      return this.rawCategoryOptions.reduce((options, group) => {
        const opts = group.options.filter((opt) => opt.type !== E_CIRCULAR_TYPES.VENDOR)
        if (opts.length > 0) {
          options.push({
            ...group,
            options: opts,
          })
        }
        return options
      }, [])
    },
    tableColumns () {
      const columns = lodash.cloneDeep(this.inTenancyAllTableColumns)
      return columns
        .map((item) => {
          if (Object.keys(this.interactionFunc).includes(item.dataIndex)) {
            item.component = Object.assign({}, item.component, {
              listeners: (_, record) => ({
                click: () => this.interactionFunc[item.dataIndex](this.getECircularByCode(record.code)),
              }),
            })
          }
          return item
        })
    },
  },
  async mounted () {
    this.tenancyId = this.$router.currentRoute.params.id
    try {
      await getTenancy(this.tenancyId, true)
    } catch (err) {
      return
    }
    const { BuildingFloorZoneMap } = await getBuildingFloorZoneMap()
    this.BuildingFloorZoneMap = BuildingFloorZoneMap
    this.searchAttributes.tenancyId = this.tenancyId
    await this.search()
  },
  methods: {
    ...mapActions('ccd', ['setECircularSearchResults']),
    ...mapMutations('ccd', {
      updateSearchCount: mutationTypes.TR_ECIRCULAR_SAVE_SEARCH_COUNT,
      updateFilterConditions: mutationTypes.TR_ECIRCULAR_SAVE_ECIRCULAR_FILTER_CONDITIONS,
      updateTableOrder: mutationTypes.TR_ECIRCULAR_SAVE_ECIRCULAR_TABLE_ORDER,
    }),
    onFilter (filterObject) {
      this.filterObject = filterObject
    },
    async getAllECircular (searchAttributes) {
      const attributes = {
        ...this.getFilterParams(searchAttributes),
        type: E_CIRCULAR_TYPES.TENANT,
      }
      return ecircularAPI.getAllECircular(attributes)
    },
    getFilterParams (searchAttributes) {
      const obj = { ...searchAttributes, ...this.filterObject }
      if (obj.category) {
        const hasAll = obj.category.includes(ALL_VALUE)
        if (hasAll) {
          obj.category = []
        }
      }
      return obj
    },
    async search () {
      this.$refs.table.scrollToTableTop()
      this.searchNext(1)
    },
    async searchNext (page) {
      this.searchAttributes.page = page
      this.searchAttributes.offset = (page - 1) * this.searchAttributes.limit
      const response = await this.getAllECircular(this.searchAttributes)
      this.updateSearchCount(response.count)
      let lastData = page === 1 ? [] : this.lastSearchedECirculars
      lastData = unionBy(lastData, response.data, (x) => x.id)
      this.setECircularSearchResults({
        lastECircularSearchAttrs: cloneDeep(this.searchAttributes),
        lastSearchedECirculars: lastData,
        lastSearchedCategories: response.resultsCategories,
      })
    },
    navBack () {
      navigation.replacePage(this.isTenant ? ccdRouteNames.ccdTenancyTenantView : ccdRouteNames.ccdTenancyView, { id: this.tenancyId, tabIndex: 1 })
    },
    gotoECircularDetail (ecircular) {
      if (this.isTenant) {
        this.showingEcircular = ecircular
        this.$refs.eCircularContentModal.open()
        return
      }
      if (ecircular.status === E_CIRCULAR_STATUS.DRAFT && this.canCreateUpdate) {
        navigation.toPage('createECircular', { id: ecircular.id, createOrEdit: 'edit' })
      } else {
        navigation.toPage('eCircularDetail', { id: ecircular.id })
      }
    },
    closeECircularContentModal () {
      this.$refs.eCircularContentModal.close()
    },
    getDisplayListLength (length) {
      this.totalLength = length
    },
    async loadMore () {
      if (!this.showLoadMore) return
      this.searchNext(this.searchAttributes.page + 1)
    },
    formatDate (item) {
      return dateUtil.formatDate(item, true)
    },
    getFilterConditions (filterConditions) {
      this.updateFilterConditions(filterConditions)
    },
    getTableOrder (orderKey, order) {
      this.updateTableOrder({orderKey, order})
    },
    getECircularByCode (code) {
      return this.lastSearchedECirculars.find((e) => code === e.code)
    },
  },
}
</script>

<style lang='scss'>
@import '~styles/e-circular/history';
.tenancy-all-ecircular {
  max-height: calc(100vh - 141px);
  .tms-header__box.tms-header__box-fullscreen {
    padding-left: 15px;
    padding-right: 15px;
  }
  .all-list {
    height: calc(100vh - 275px);
    margin: 15px 15px 0 15px !important;
  }
  .table-list {
    border: none;
  }
  .hkl-table {
    height: calc(100vh - 351px);
    margin-left: 0;
    margin-right: 0;
    .underline {
      text-decoration: underline;
      cursor: pointer;
    }
  }
}
</style>
